import { useEffect, useState } from 'react';

interface HeroProps {
  onStartClick: () => void;
}

export default function Hero({ onStartClick }: HeroProps) {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const imageTransform = Math.min(scrollY * 0.3, 100);

  return (
    <section id="home" className="relative min-h-screen bg-gradient-to-br from-[#0A0A0A] via-[#1A1A1A] to-[#0F0F0F] overflow-hidden">
      {/* 배경 이미지 */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/d6c08f033ff7fddc110d7159fe66137b.jpg"
          alt="배경"
          className="w-full h-full object-cover opacity-40"
          loading="eager"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/70 via-[#1A1A1A]/60 to-[#0F0F0F]/70"></div>
      </div>
      
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#14B8A6]/5 via-transparent to-transparent"></div>
      
      {/* LEXO 배경 워터마크 */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[100px] sm:text-[200px] lg:text-[400px] font-black text-white/[0.02] select-none pointer-events-none tracking-tighter">
        LEXO
      </div>
      
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 pt-20 sm:pt-32 pb-16 sm:pb-20 min-h-screen flex items-center">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
          <div className="space-y-4 sm:space-y-8 text-center lg:text-left">
            {/* LEXO 브랜드 배지 - 강한 일본어 슬로건 추가 */}
            <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-1.5 sm:py-2.5 bg-gradient-to-r from-[#14B8A6]/10 to-[#0D9488]/10 border border-[#14B8A6]/30 rounded-full backdrop-blur-sm">
              <i className="ri-flashlight-fill text-[#14B8A6] text-xs sm:text-sm"></i>
              <span className="text-[11px] sm:text-[14px] font-semibold text-[#14B8A6] tracking-wide">강한 일본어</span>
            </div>

            {/* LEXO 대형 타이틀 추가 */}
            <div className="space-y-2">
              <h1 className="text-[48px] sm:text-[90px] lg:text-[120px] font-black bg-gradient-to-r from-[#14B8A6] via-[#0D9488] to-[#14B8A6] bg-clip-text text-transparent leading-[1] tracking-tighter">
                LEXO
              </h1>
            </div>

            <div className="space-y-1 sm:space-y-3">
              <h1 className="text-[28px] sm:text-[52px] lg:text-[68px] font-bold text-white leading-[1.1] tracking-tight">
                영상으로 배우는
              </h1>
              <h1 className="text-[32px] sm:text-[60px] lg:text-[80px] font-bold text-white leading-[1.1] tracking-tight">
                진짜
              </h1>
              <h1 className="text-[36px] sm:text-[68px] lg:text-[92px] font-black bg-gradient-to-r from-[#14B8A6] via-[#0D9488] to-[#14B8A6] bg-clip-text text-transparent leading-[1.1] tracking-tight">
                강한 일본어
              </h1>
            </div>

            <p className="text-[14px] sm:text-[18px] text-gray-300 leading-relaxed max-w-lg pt-1 sm:pt-2 mx-auto lg:mx-0">
              유튜브 영상과 핵심 표현으로<br className="hidden sm:block" />
              <span className="text-[#14B8A6] font-semibold">실전 일본어 실력</span>을 키워보세요
            </p>

            {/* 신뢰 지표 */}
            <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-6 pt-4 sm:pt-10">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#14B8A6] to-[#0D9488] border-2 border-[#0A0A0A]"></div>
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#0D9488] to-[#14B8A6] border-2 border-[#0A0A0A]"></div>
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#14B8A6] to-[#0D9488] border-2 border-[#0A0A0A]"></div>
                </div>
                <span className="text-[11px] sm:text-[13px] text-gray-400"><span className="text-white font-semibold">5,000+</span> 학습자</span>
              </div>
              <div className="h-4 w-px bg-gray-700"></div>
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-[#14B8A6] text-[12px] sm:text-[14px]"></i>
                <span className="text-[11px] sm:text-[13px] text-gray-400"><span className="text-white font-semibold">4.9</span> 만족도</span>
              </div>
            </div>

            {/* CTA 버튼 */}
            <div className="flex justify-center lg:justify-start pt-2 sm:pt-4">
              <button
                onClick={onStartClick}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white font-bold text-[15px] sm:text-[17px] rounded-full shadow-lg shadow-[#14B8A6]/30 hover:shadow-[#14B8A6]/50 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-right-circle-fill text-lg"></i>
                지금 시작하기
              </button>
            </div>
          </div>

          <div className="relative mt-4 lg:mt-0">
            {/* 한자 - 이미지 앞쪽 왼쪽 상단 */}
            <div 
              className="absolute top-3 sm:top-10 -left-2 sm:-left-5 z-20 w-16 h-16 sm:w-28 sm:h-28 bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] border border-[#14B8A6]/40 rounded-xl sm:rounded-3xl shadow-xl shadow-[#14B8A6]/10 flex items-center justify-center transform -rotate-6 animate-float-kanji backdrop-blur-sm"
              style={{ transform: `translateY(-${imageTransform}px) rotate(-6deg)` }}
            >
              <span className="text-xl sm:text-4xl font-bold text-[#14B8A6]">漢字</span>
            </div>

            {/* 히라가나 - 이미지 앞쪽 오른쪽 상단 */}
            <div 
              className="absolute top-8 sm:top-20 -right-2 sm:-right-8 z-20 w-16 h-16 sm:w-28 sm:h-28 bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] border border-[#0D9488]/40 rounded-xl sm:rounded-3xl shadow-xl shadow-[#0D9488]/10 flex items-center justify-center transform rotate-12 animate-float-hiragana backdrop-blur-sm"
              style={{ transform: `translateY(-${imageTransform * 0.8}px) rotate(12deg)` }}
            >
              <span className="text-xl sm:text-4xl font-bold text-[#0D9488]">あいう</span>
            </div>

            {/* 가타카나 - 이미지 앞쪽 하단 */}
            <div 
              className="absolute bottom-16 sm:bottom-32 -left-3 sm:-left-10 z-20 w-16 h-16 sm:w-28 sm:h-28 bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] border border-[#14B8A6]/40 rounded-xl sm:rounded-3xl shadow-xl shadow-[#14B8A6]/10 flex items-center justify-center transform rotate-6 animate-float-katakana backdrop-blur-sm"
              style={{ transform: `translateY(-${imageTransform * 1.2}px) rotate(6deg)` }}
            >
              <span className="text-xl sm:text-4xl font-bold text-[#14B8A6]">アイウ</span>
            </div>

            <div 
              className="relative z-10 transition-transform duration-300 max-w-[280px] sm:max-w-md mx-auto lg:max-w-none"
              style={{ transform: `translateY(-${imageTransform * 0.5}px) rotate(2deg)` }}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=korean%20young%20professional%20woman%20looking%20at%20smartphone%20screen%20showing%20youtube%20video%20thumbnail%20with%20japanese%20learning%20materials%20and%20japanese%20text%20expressions%2C%20one%20way%20content%20delivery%20from%20LEXO%20channel%2C%20teal%20green%20accent%20colors%2C%20clean%20modern%20minimalist%20design%2C%20soft%20natural%20lighting%2C%20professional%20lifestyle%20photography%20showing%20satisfied%20learner%20receiving%20educational%20content&width=600&height=800&seq=hero-korean-receiving-lexo-v3&orientation=portrait" 
                alt="LEXO App Mockup"
                className="w-full h-auto hover:rotate-0 transition-transform duration-700 drop-shadow-2xl"
                loading="eager"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float-kanji {
          0%, 100% { transform: translateY(0px) rotate(-6deg); }
          50% { transform: translateY(-20px) rotate(-6deg); }
        }
        @keyframes float-hiragana {
          0%, 100% { transform: translateY(0px) rotate(12deg); }
          50% { transform: translateY(-25px) rotate(12deg); }
        }
        @keyframes float-katakana {
          0%, 100% { transform: translateY(0px) rotate(6deg); }
          50% { transform: translateY(-18px) rotate(6deg); }
        }
        .animate-float-kanji {
          animation: float-kanji 3s ease-in-out infinite;
        }
        .animate-float-hiragana {
          animation: float-hiragana 3.5s ease-in-out infinite 0.5s;
        }
        .animate-float-katakana {
          animation: float-katakana 2.8s ease-in-out infinite 1s;
        }
      `}</style>
    </section>
  );
}
